/**
 * Contacts — `docs/API.md` §5.
 *
 * Fully implemented server-side: list (+ `/query` and its two aggregates),
 * create, read, patch, delete, restore, duplicate-check, and the
 * relationship/account-link sub-resources all live in
 * `salescrmapi/src/modules/contacts`. `merge` and the `activities`/`documents`
 * sub-resources answer `501 not_implemented`, as does `wish` — sending an
 * actual greeting needs the email module (§8), which isn't wired up.
 *
 * `GET /contacts/anniversaries` was added alongside this screen: the birthday/
 * work/customer-anniversary digest that §5 names but the module didn't serve
 * yet. See `salescrmapi/docs/modules/contacts.model.md`.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listContactsQuery's fields, e.g. `{ q, account_id, buying_role }` */
export function listContacts(params = {}) {
  return withMockFallback(
    'GET /contacts',
    async () => (await apiClient.get('/contacts', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 25, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}

/**
 * One contact. `include` may carry `accounts` and/or `relationships`.
 * @param {string} id
 * @param {{include?: string[]}} [options]
 */
export function getContact(id, { include } = {}) {
  return withMockFallback(
    `GET /contacts/${id}`,
    async () => (await apiClient.get(`/contacts/${id}`, {
      params: include?.length ? { include: include.join(',') } : undefined,
    })).data,
    () => null,
  );
}

/**
 * Create a contact. Carries an Idempotency-Key so a double-submit cannot
 * create the same person twice.
 * @param {object} payload at minimum `{ last_name }`
 */
export function createContact(payload) {
  return withMockFallback(
    'POST /contacts',
    async () =>
      (await apiClient.post('/contacts', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: `CT-${Math.floor(Math.random() * 90000 + 10000)}`, row_version: 1 }),
  );
}

/**
 * Update a contact under optimistic locking (§1.7).
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export function updateContact(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /contacts/${id}`,
    async () =>
      (await apiClient.patch(`/contacts/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Soft-delete a contact.
 * @param {string} id
 * @param {{reason:string, rowVersion?:number}} options
 */
export function deleteContact(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /contacts/${id}`,
    async () =>
      (await apiClient.delete(`/contacts/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** Undo a soft delete. @param {string} id */
export function restoreContact(id) {
  return withMockFallback(
    `POST /contacts/${id}/restore`,
    async () => (await apiClient.post(`/contacts/${id}/restore`)).data,
    () => ({ id, deleted: false }),
  );
}

/**
 * Duplicate check. Pass `'new'` as `id` while the record doesn't exist yet
 * (the create form's live check) or a real id to re-check an existing one.
 * @param {string} id
 * @param {{email?:string, phone?:string, last_name?:string, account_id?:string}} [candidate]
 */
export function checkContactDuplicates(id, candidate = {}) {
  return withMockFallback(
    `GET /contacts/${id}/duplicates`,
    async () => (await apiClient.get(`/contacts/${id}/duplicates`, { params: candidate })).data,
    () => ({ matches: [] }),
  );
}

/** Birthdays and work/customer anniversaries due within `withinDays`, soonest first. */
export function listContactAnniversaries(withinDays = 30) {
  return withMockFallback(
    'GET /contacts/anniversaries',
    async () => (await apiClient.get('/contacts/anniversaries', { params: { within_days: withinDays } })).data,
    () => ({ data: [] }),
  );
}

/** Contact-to-contact edges: 'reports to', 'influences', 'ally of', etc. @param {string} id */
export function listContactRelationships(id) {
  return withMockFallback(
    `GET /contacts/${id}/relationships`,
    async () => (await apiClient.get(`/contacts/${id}/relationships`)).data,
    () => ({ data: [] }),
  );
}

/**
 * @param {string} id
 * @param {{to_contact_id:string, relationship:string, strength?:string, notes?:string}} payload
 */
export function addContactRelationship(id, payload) {
  return withMockFallback(
    `POST /contacts/${id}/relationships`,
    async () => (await apiClient.post(`/contacts/${id}/relationships`, payload)).data,
    () => ({ ...payload, id: crypto.randomUUID() }),
  );
}

/** @param {string} id @param {string} relationshipId */
export function removeContactRelationship(id, relationshipId) {
  return withMockFallback(
    `DELETE /contacts/${id}/relationships/${relationshipId}`,
    async () => (await apiClient.delete(`/contacts/${id}/relationships/${relationshipId}`)).data,
    () => ({ deleted: true }),
  );
}

/** The accounts this contact sits on, beyond their one primary employer. @param {string} id */
export function listContactAccounts(id) {
  return withMockFallback(
    `GET /contacts/${id}/accounts`,
    async () => (await apiClient.get(`/contacts/${id}/accounts`)).data,
    () => ({ data: [] }),
  );
}

/** @param {string} id @param {{account_id:string, role?:string}} payload */
export function linkContactAccount(id, payload) {
  return withMockFallback(
    `POST /contacts/${id}/accounts`,
    async () => (await apiClient.post(`/contacts/${id}/accounts`, payload)).data,
    () => ({ ...payload }),
  );
}

/** @param {string} id @param {string} accountId */
export function unlinkContactAccount(id, accountId) {
  return withMockFallback(
    `DELETE /contacts/${id}/accounts/${accountId}`,
    async () => (await apiClient.delete(`/contacts/${id}/accounts/${accountId}`)).data,
    () => ({ deleted: true }),
  );
}
